import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api"; // Your API service
import { getCurrentMonthDateRange } from "../../utils/dateRange";
import { getPharmacyId } from "../../utils/commonFunctions"; // Utility function
import {
  GET_BOOKING_STATS_URL,
  GET_EMPLOYEE_DASHBOARD_DATA,
  GET_PHARMACY_LEADERBOARD_ADMIN_URL,
} from "../../services/urls";

export const LEADERBOARD_PERIODS = {
  THIS_MONTH: "this_month",
  LAST_MONTH: "last_month",
  THIS_WEEK: "this_week",
  LAST_WEEK: "last_week",
};

// Initial state for the slice
const initialState = {
  loading: false,
  statsLoading: false,
  leaderboardLoading: false,
  leaderboard: null,
  leaderboardError: null,
  stats: {
    totalPharmacies: 0,
    totalUsers: 0,
    totalBooking: 0,
    totalUnattendedBooking: 0,
    totalCancelledBooking: 0,
    totalOpenBooking: 0,
    totalFinishedBooking: 0,
    totalRevenue: 0,
    missedRevenue: 0, // Added missedRevenue field
    timeSeriesData: [], // To store time series data for bookings & revenue
  },
  pieChartData: {
    labels: ["Finished", "Open", "Cancelled", "Unattended"], // Added "Missed Revenue"
    data: [0, 0, 0, 0], // Data based on the stats
  },
  labTestBookings: [], // Holds lab test booking data
  topLabTests: [], // Holds top 3 lab tests
  error: null,
  employeeDashboardData: [],
  employeeDashboardLoading: false,
};

// Async thunk to fetch booking stats and time series data
export const fetchEmployeeDashboardList = createAsyncThunk(
  "stats/fetchEmployeeDashboardList",
  async ({ date }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        GET_EMPLOYEE_DASHBOARD_DATA.replace(":pharmacyId", getPharmacyId()),
        {
          params: {
            date,
          },
        }
      );
      return response.data?.data?.bookings?.data || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch booking stats"
      );
    }
  }
);

// Async thunk to fetch booking stats and time series data
export const fetchBookingStats = createAsyncThunk(
  "stats/fetchBookingStats",
  async (selectedDates, { rejectWithValue }) => {
    try {
      const defaultRange = getCurrentMonthDateRange();
      const response = await api.get(
        GET_BOOKING_STATS_URL,
        {
          params: {
            startDate: selectedDates?.startDate ?? defaultRange.startDate,
            endDate: selectedDates?.endDate ?? defaultRange.endDate,
          },
        }
      ); // Replace with the correct API route
      const stats = response.data?.data;

      // Calculate pie chart data
      const pieChartData = {
        labels: ["Finished", "Open", "Cancelled", "Unattended"], // Updated labels to include "Missed Revenue"
        data: [
          stats?.totalFinishedBooking,
          stats?.totalOpenBooking,
          stats?.totalCancelledBooking,
          stats?.totalUnattendedBooking,
        ],
      };

      // Return the updated stats data, pie chart data, and lab test data
      return {
        stats,
        pieChartData,
        labTestBookings: stats?.labTestBookings,
        topLabTests: stats?.topLabTests,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch booking stats"
      );
    }
  }
);

export const fetchAdminPharmacyLeaderboard = createAsyncThunk(
  "stats/fetchAdminPharmacyLeaderboard",
  async (period, { rejectWithValue }) => {
    try {
      const response = await api.get(GET_PHARMACY_LEADERBOARD_ADMIN_URL, {
        params: { period },
      });
      return response.data?.data || null;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pharmacy leaderboard"
      );
    }
  }
);

const statsSlice = createSlice({
  name: "stats",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookingStats.pending, (state) => {
        state.loading = true;
        state.statsLoading = true;
      })
      .addCase(fetchBookingStats.fulfilled, (state, action) => {
        state.loading = false;
        state.statsLoading = false;
        state.error = null;
        // Set the fetched stats, pie chart data, and lab test data in state
        state.stats = action.payload.stats;
        state.pieChartData = action.payload.pieChartData;
        state.labTestBookings = action.payload.labTestBookings;
        state.topLabTests = action.payload.topLabTests;
      })
      .addCase(fetchBookingStats.rejected, (state, action) => {
        state.loading = false;
        state.statsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchEmployeeDashboardList.pending, (state) => {
        state.employeeDashboardLoading = true;
      })
      .addCase(fetchEmployeeDashboardList.fulfilled, (state, action) => {
        state.employeeDashboardLoading = false;
        state.error = null;
        state.employeeDashboardData = action.payload;
      })
      .addCase(fetchEmployeeDashboardList.rejected, (state, action) => {
        state.employeeDashboardLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchAdminPharmacyLeaderboard.pending, (state) => {
        state.leaderboardLoading = true;
        state.leaderboardError = null;
      })
      .addCase(fetchAdminPharmacyLeaderboard.fulfilled, (state, action) => {
        state.leaderboardLoading = false;
        state.leaderboard = action.payload;
      })
      .addCase(fetchAdminPharmacyLeaderboard.rejected, (state, action) => {
        state.leaderboardLoading = false;
        state.leaderboardError = action.payload;
      });
  },
});

export default statsSlice.reducer;
